import { clsx } from '@alexis/helpers/clsx';
import { countriesConfig } from '@alexis/helpers/country';
import type { PhoneNumberInputProps } from 'props/phoneNumberInputProps';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { AutoSizer, List, type ListRowProps, type Size } from 'react-virtualized';

import type { Country } from '@wegoTypes/country';
import { TranslationsKey } from '@wegoTypes/translations';

import CircleTickIcon from '../icons/circle_tick.svg';
import WarningIcon from '../icons/warning.svg';
import styles from '../styles/components/PhoneNumberInput.module.scss';

const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (
    {
      className,
      countryCallingCodeInputId,
      countryCallingCodeInputName,
      currentSite,
      isPhoneNumberValidated,
      message,
      onCountrySelected,
      onPhoneNumberBlur,
      onPhoneNumberChange,
      phoneNumberhasError,
      phoneNumberInputId,
      phoneNumberInputName,
      placeholder,
      callingCodeLabel,
      selectedCountry,
      style,
      translations,
      value,
      hideSelectedCountryFlag,
    },
    ref,
  ): JSX.Element => {
    const [isCallingCodeFocus, setIsCallingCodeFocus] = useState<boolean>(false);
    const [isPhoneNumberFocus, setIsPhoneNumberFocus] = useState<boolean>(false);

    const [callingCode, setCallingCode] = useState<string>('');

    const countries = useMemo<Array<Country>>(() => {
      const currentSiteCountry = countriesConfig.splice(
        countriesConfig.findIndex((country) => country.code === currentSite.countryCode),
        1,
      );

      countriesConfig.unshift(currentSiteCountry[0]);

      return countriesConfig;
    }, [currentSite]);

    const filterCountries = useMemo<Array<Country>>(() => {
      if (!callingCode) {
        return countries;
      } else {
        const lowerCaseSearchQuery = callingCode.toLowerCase().replace(/\+/g, '').trim();

        return countries.filter((country) => {
          const countryName = country.name.toLowerCase();
          const countryCode = country.code.toLowerCase();
          const countryNameLocale = (
            translations[`pos_${countryCode}` as TranslationsKey] as string
          ).toLowerCase();
          const callingCode = country.callingCode.toString();

          return (
            countryName.startsWith(lowerCaseSearchQuery) ||
            countryCode.startsWith(lowerCaseSearchQuery) ||
            countryNameLocale.startsWith(lowerCaseSearchQuery) ||
            callingCode.startsWith(lowerCaseSearchQuery)
          );
        });
      }
    }, [countries, callingCode, translations]);

    // Set is calling code focus to false if body is clicked
    useEffect(() => {
      if (isCallingCodeFocus) {
        const handleBodyOnClick = (): void => {
          setIsCallingCodeFocus(false);
        };

        const timerId = setTimeout(() => {
          document.body.addEventListener('click', handleBodyOnClick);
        }, 0);

        return () => {
          clearTimeout(timerId);
          document.body.removeEventListener('click', handleBodyOnClick);
        };
      }
    }, [isCallingCodeFocus]);

    useEffect(() => {
      if (isCallingCodeFocus) {
        setCallingCode('');
      } else {
        setCallingCode(`+${selectedCountry.callingCode}`);
      }
    }, [isCallingCodeFocus, selectedCountry]);

    const handleCountrySelected = useCallback<(country: Country) => void>(
      (country: Country) => {
        onCountrySelected(country);
        setIsCallingCodeFocus(false);
      },
      [onCountrySelected],
    );

    const handleCountryCallingCodeKeyDown = useCallback<
      (event: React.KeyboardEvent<HTMLInputElement>) => void
    >(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();

          if (!!event.currentTarget.value && filterCountries.length > 0) {
            handleCountrySelected(filterCountries[0]);
            event.currentTarget.blur();
          }
        }
      },
      [filterCountries, handleCountrySelected],
    );

    const rowRenderer = useCallback<({ key, index, style }: ListRowProps) => JSX.Element>(
      ({ key, index, style }: ListRowProps) => {
        const country = filterCountries[index];
        const countryCode = country.code.toLowerCase();

        return (
          <div key={key} style={style}>
            <div className={styles.country} onClick={() => handleCountrySelected(country)}>
              <div className={styles.countryFlagAndNameContainer}>
                <i
                  className={clsx(
                    styles.countryFlags,
                    styles[countryCode],
                    styles.medium,
                    styles.withLightBackground,
                  )}
                ></i>
                <div className={styles.name}>
                  {translations[country.label as TranslationsKey] as string}
                </div>
              </div>

              <div className={styles.countryCallingCode}>+{country.callingCode}</div>
            </div>
          </div>
        );
      },
      [filterCountries, handleCountrySelected, translations],
    );

    const handlePhoneNumberBlur = useCallback<() => void>(() => {
      setIsPhoneNumberFocus(false);

      if (!!onPhoneNumberBlur) {
        onPhoneNumberBlur();
      }
    }, [onPhoneNumberBlur]);

    const handlePhoneNumberFocus = useCallback<() => void>(() => {
      setIsCallingCodeFocus(false);
      setIsPhoneNumberFocus(true);
    }, []);

    const defaultCountryCallingCodeInputId = 'countryCallingCode';
    const defaultPhoneNumberInputId = 'phoneNumber';

    return (
      <div className={clsx(styles.container, className)} style={{ ...style, direction: 'ltr' }}>
        <div
          className={clsx(
            styles.callingCode,
            isCallingCodeFocus && styles.expanded,
            hideSelectedCountryFlag && styles.hideSelectedCountryFlag,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideSelectedCountryFlag && (
            <i
              className={clsx(
                styles.countryFlags,
                styles[selectedCountry.code.toLowerCase()],
                styles.medium,
                styles.withLightBackground,
              )}
            ></i>
          )}

          {!!callingCodeLabel && (
            <label htmlFor={countryCallingCodeInputId ?? defaultCountryCallingCodeInputId}>
              {callingCodeLabel}
            </label>
          )}

          <input
            className={clsx(hideSelectedCountryFlag && styles.hideSelectedCountryFlag)}
            autoComplete='tel-country-code'
            id={countryCallingCodeInputId ?? defaultCountryCallingCodeInputId}
            inputMode='numeric'
            name={countryCallingCodeInputName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCallingCode(e.target.value)}
            onFocus={() => setIsCallingCodeFocus(true)}
            onKeyDown={handleCountryCallingCodeKeyDown}
            spellCheck={false}
            type='text'
            value={callingCode}
          />

          <i className={clsx(styles.triangle, styles.down)}></i>

          {isCallingCodeFocus ? (
            <div
              className={styles.dropdown}
              style={{ height: `${Math.min(5, filterCountries.length) * 48 + 24}px` }}
            >
              <AutoSizer>
                {({ height, width }: Size) => (
                  <List
                    height={height}
                    overscanRowCount={5}
                    rowCount={filterCountries.length}
                    rowHeight={48}
                    rowRenderer={rowRenderer}
                    tabIndex={null}
                    width={width}
                  />
                )}
              </AutoSizer>
            </div>
          ) : null}
        </div>

        <div
          className={clsx(
            styles.phoneNumberAndMessage,
            isPhoneNumberFocus && styles.focus,
            isPhoneNumberValidated && styles.validated,
            !!phoneNumberhasError && styles.error,
          )}
        >
          <div className={styles.input}>
            <input
              autoComplete='tel-national'
              id={phoneNumberInputId ?? defaultPhoneNumberInputId}
              inputMode='numeric'
              name={phoneNumberInputName}
              onBlur={handlePhoneNumberBlur}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onPhoneNumberChange(e.target.value)
              }
              onFocus={handlePhoneNumberFocus}
              ref={ref}
              spellCheck={false}
              type='tel'
              value={value}
            />

            <label htmlFor={phoneNumberInputId ?? defaultPhoneNumberInputId}>{placeholder}</label>

            {isPhoneNumberValidated && !phoneNumberhasError ? <CircleTickIcon /> : null}

            {isPhoneNumberValidated && !!phoneNumberhasError ? <WarningIcon /> : null}
          </div>

          <div className={styles.message}>{message}</div>
        </div>
      </div>
    );
  },
);

export default PhoneNumberInput;
